import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Maximize2, Minimize2, RotateCw, VideoOff } from "lucide-react";
import { t } from "@client/lib/dictionary";
import { cn } from "@client/lib/cn";
import { tileFills, type CameraTileMode } from "@client/lib/foscam/cameraLayout";
import { useAutoHide } from "@client/hooks/useAutoHide";
import { useAim } from "@client/hooks/useAim";
import { useDigitalZoom } from "@client/hooks/useDigitalZoom";
import { usePtzHold } from "@client/hooks/usePtzHold";
import { useFullscreen } from "@client/hooks/useFullscreen";
import { useWebrtcStats } from "@client/hooks/useWebrtcStats";
import { useFoscamStore } from "@client/store/foscam";
import { useUIStore } from "@client/store/ui";
import { WEBRTC_FALLBACK_MS } from "@client/lib/foscam/streamStats";
import type { VideoRTC } from "@client/vendor/go2rtc/video-rtc";
import CameraStream, { type CameraStreamState } from "./CameraStream";
import CameraControls from "./CameraControls";

/**
 * Jedna kamera: přehrávač + chrome.
 *
 * SPOTLIGHT BEZ RESTARTU STREAMU — klíčové rozhodnutí. Spotlight není portál ani
 * nová instance přehrávače: React neumí reparentovat DOM, takže přesun do jiného
 * containeru by znamenal nový `<video>`, nové spojení a několik sekund černa. Místo
 * toho se TENTÝŽ div jen promuje CSS na `fixed inset-0`. React strom se nemění →
 * media element je fyzicky pořád ten samý → obraz běží dál.
 *
 * Nativní Fullscreen API se navrch zkusí (`useFullscreen`) — kromě skrytí UI
 * prohlížeče má i technický přínos: fullscreen element se renderuje v top layeru,
 * takže obejde případný `transform` na předkovi, který by `position: fixed` ukotvil.
 * Když nativní FS není (iPhone), CSS promoce funguje sama.
 *
 * Chrome podle režimu:
 *  - `grid`    → JEN tlačítko fullscreenu. Žádné PTZ, žádné presety, žádný kodek.
 *  - `stacked` → tlačítko fullscreenu + plné ovládání.
 *  - spotlight → tlačítko zavřít + plné ovládání.
 *
 * Ve všech třech případech platí jedno pravidlo viditelnosti: v klidu ztlumit na
 * 20 %, při pohybu/tapu zpět na 100 %, nikdy neschovat úplně (viz `useAutoHide`).
 */

/**
 * Stav streamu se neukazuje textem, ale opacitou obrazu — je to čitelné na první
 * pohled a nepřekrývá to scénu. Ztlumení je schválně jemné.
 */
const STATE_OPACITY: Record<CameraStreamState, number> = {
  playing: 1,
  loading: 0.82,
  stalled: 0.82,
  error: 0.55,
};

interface Props {
  slug: string;
  name: string;
  mode: CameraTileMode;
  spotlighted: boolean;
  onSpotlight: () => void;
  onExitSpotlight: () => void;
}

export const CameraTile: React.FC<Props> = ({ slug, name, mode, spotlighted, onSpotlight, onExitSpotlight }) => {
  const tileRef = useRef<HTMLDivElement | null>(null);
  /** Rám kolem videa — ořezává výřez a nese gesture listenery digitálního zoomu. */
  const frameRef = useRef<HTMLDivElement | null>(null);
  /** Vnitřní <video>. Do něj zapisuje digitální zoom svou transformaci. */
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const engineRef = useRef<VideoRTC | null>(null);
  const [controlsBusy, setControlsBusy] = useState(false);
  const [streamState, setStreamState] = useState<CameraStreamState>("loading");
  /** Ruční restart streamu — změna klíče přemountuje `CameraStream`. */
  const [retryKey, setRetryKey] = useState(0);

  /** Obraz vyplňuje buňku (všude kromě `grid`), ne přirozenou výšku videa. */
  const fill = tileFills(mode, spotlighted);
  const showControls = fill;

  const getEngine = useCallback(() => engineRef.current, []);
  const stats = useWebrtcStats(getEngine, showControls);
  // Dojezd obrazu po puštění PTZ tlačítka. Bez měření (ještě nedorazil snímek)
  // se použije typická hodnota WebRTC — automat si ji stejně klampuje na 1 s.
  const latencyMs = stats.latencyMs ?? WEBRTC_FALLBACK_MS;

  // Callback v refu, aby `useFullscreen` nemusel re-registrovat listenery.
  // Zápis v efektu (React 19 compiler: ref se v renderu nemodifikuje).
  const onExitRef = useRef(onExitSpotlight);
  useEffect(() => {
    onExitRef.current = onExitSpotlight;
  }, [onExitSpotlight]);
  const fs = useFullscreen(tileRef, {
    // Orientaci NEZAMYKÁME. Vynucený landscape vylučuje případ „vysoká obrazovka",
    // kvůli kterému ovládání sedí na spodní hraně — na výšku patří do černého pruhu
    // pod obrazem, na šířku přes jeho spodek. Telefon zůstane, jak ho uživatel drží.
    lockLandscape: false,
    lockScroll: true,
    // Spotlight je vnitřní vrstva — Esc musí zavřít jen jeho, ne i fullscreen
    // wrapperu, ve kterém může být vnořený.
    escCapture: true,
    onExit: () => onExitRef.current(),
  });

  // Prop `spotlighted` (vlastněná rodičem, aby svítila jen jedna kamera) řídí nativní FS.
  // `entered` guard je nutný: bez něj by `exit()` při mountu shodilo fullscreen
  // wrapperu, ve kterém dlaždice žije.
  const entered = useRef(false);
  const { enter, exit } = fs;
  useEffect(() => {
    if (spotlighted && !entered.current) {
      entered.current = true;
      enter();
    } else if (!spotlighted && entered.current) {
      entered.current = false;
      exit();
    }
  }, [spotlighted, enter, exit]);

  const { opacity, poke } = useAutoHide(3_000, controlsBusy);

  const editMode = useUIStore((s) => s.editMode);
  const caps = useFoscamStore((s) => s.caps[slug]);
  const hasPtz = caps?.hasPtz ?? false;

  /**
   * PTZ držení žije nově tady, ne v `CameraControls`.
   *
   * Důvod: ovládat kameru chtějí dva různé prvky — směrový kříž a edge-push
   * digitálního zoomu. `ptzStopRun` je na kameře globální, takže musí existovat jediná
   * autorita, která hlídá „jedna osa v jednu chvíli". Dvě instance hooku by si navzájem
   * přebíjely stop.
   */
  const hold = usePtzHold(slug, latencyMs);

  // Digitální zoom. V editoru layoutu se gesta musí úplně vypnout — widgety tam mají
  // `pointer-events-none` a binder by se pral s drag handlem editoru.
  const aim = useAim(slug, hasPtz);

  const streamKey = `${slug}-${retryKey}`;
  const zoom = useDigitalZoom({
    frameRef,
    videoRef,
    videoKey: streamKey,
    fit: fill ? "contain" : "none",
    enabled: !editMode,
    // U PTZ kamery dvojklik míří, u pevné resetuje zoom (proto ten návratový příznak).
    // Bod se posílá v souřadnicích OBRAZU, takže míření funguje i zazoomované.
    onDoubleTap: hasPtz ? (point, local) => aim.aimAt(point, local) : undefined,
    onEdgePush: hasPtz ? hold.nudge : undefined,
  });

  const shell = cn(
    // `@container`: chrome se dimenzuje podle šířky DLAŽDICE, ne okna. Stejná dlaždice
    // má 183 px (2×2 na telefonu), ~400 px (desktop) i celou obrazovku (spotlight) —
    // a jen ona sama ví, kolik místa tlačítko smí zabrat.
    "@container bg-black",
    spotlighted
      ? "fixed inset-0 z-60 flex flex-col pt-[env(safe-area-inset-top)]"
      : mode === "stacked"
        ? "relative flex h-full w-full shrink-0 snap-start flex-col"
        : mode === "quad-fs"
          ? // Buňku vymezuje `auto-rows-fr` na kontejneru; dlaždice ji jen vyplní.
            "relative flex h-full min-h-0 w-full flex-col"
          : mode === "quad"
            ? // Mimo fullscreen výšku nikdo neurčuje, takže si ji buňka musí dát sama —
              // jinak by ji určovalo video a mřížka by měla rozhozené řady.
              "relative flex aspect-video w-full flex-col"
            : // Pod 640 px dvě kamery na řádek (2×2), od `sm` výš všechny do jednoho řádku.
              // `grow` až od `sm`: bez toho by se při lichém počtu kamer poslední dlaždice
              // na telefonu roztáhla přes celý řádek a byla dvakrát větší než ostatní.
              "relative basis-1/2 sm:basis-0 sm:grow"
  );

  /** Podklad ikony. Na úzké dlaždici drobný, od 300 px šířky plných 44 px. */
  const chromeChip =
    "inline-flex h-7 w-7 items-center justify-center rounded-md bg-black/55 text-white backdrop-blur-sm " +
    "@[300px]:h-11 @[300px]:w-11 hover:bg-black/80 touch-manipulation";
  const chromeIcon = "h-4 w-4 @[300px]:h-5 @[300px]:w-5";

  const chromeStyle = { opacity, transition: "opacity 200ms ease-out" } as const;

  return (
    <>
      {/* Ztmavení zbytku appky. V portálu, aby nebylo ovlivněné stackingem předků. */}
      {spotlighted && createPortal(<div className="fixed inset-0 z-55 bg-black/90" aria-hidden="true" />, document.body)}

      <div
        ref={tileRef}
        className={shell}
        // Ve stacked režimu se dlaždice nesmí roztáhnout na absurdní šířku na velkém
        // monitoru — obraz by měl výšku 200 px a zbytek by byl černý.
        style={
          mode === "stacked" && !spotlighted
            ? { maxWidth: "min(100%, calc((100dvh - 6rem) * 16 / 9))", marginInline: "auto" }
            : undefined
        }
        onPointerMove={poke}
        onPointerDown={poke}
      >
        {/* Box s obrazem. Ve fill režimech vyplní zbytek dlaždice, ovládání je až
            pod ním (jako sourozenec), aby sedělo na spodní hraně DLAŽDICE. */}
        <div
          ref={frameRef}
          className={cn(
            // `overflow-hidden` je to, co drží slib „dlaždice zabírá pořád stejně místa":
            // transform je čistě malířský, layoutový box videa se nemění, takže zvětšený
            // obraz se jen ořízne na dosavadní rozměr rámu. Nemůže to rozbít promoci
            // dlaždice na `position: fixed` — ta je PŘEDEK rámu, ne potomek.
            "relative w-full overflow-hidden",
            fill && "min-h-0 flex-1",
            // Dokud není zazoomováno, svislé tažení patří scrollu (seznam kamer ve
            // fullscreenu wrapperu, stránka v mřížce). Po zoomu si ho bere posun výřezu.
            // Ve spotlightu není kam scrollovat, ale `pan-y` by tam pustilo nativní
            // pinch-zoom prohlížeče — proto rovnou `none`.
            spotlighted || zoom.isZoomed ? "touch-none" : "touch-pan-y"
          )}
        >
          <div className="h-full w-full" style={{ opacity: STATE_OPACITY[streamState], transition: "opacity 500ms ease-in-out" }}>
            <CameraStream
              key={streamKey}
              ref={videoRef}
              slug={slug}
              muted
              playsInline
              onState={setStreamState}
              onEngine={(el) => {
                engineRef.current = el;
              }}
              className={fill ? "block h-full w-full" : "block w-full"}
              style={fill ? { objectFit: "contain" } : undefined}
            />
          </div>

          {/* Stream se nepodařilo navázat — nabídni ruční restart.
              `z-20` (nad celodlaždicovým tlačítkem v mřížce) + `pointer-events-none` na
              podkladu: tlačítko *Zkusit znovu* je klikatelné, ale tap mimo něj pořád
              propadne na tlačítko fullscreenu pod ním. */}
          {streamState === "error" && (
            <div className="pointer-events-none absolute inset-0 z-20 flex flex-col items-center justify-center gap-2 bg-black/80 p-4 text-center">
              <VideoOff className="h-6 w-6 text-status-warning" />
              <p className="text-xs font-medium text-white">{t.foscam.streamError}</p>
              <button
                type="button"
                className="pointer-events-auto inline-flex h-9 items-center gap-1.5 rounded-md bg-white/15 px-3 text-xs font-medium text-white hover:bg-white/25"
                onClick={() => setRetryKey((k) => k + 1)}
              >
                <RotateCw className="h-3.5 w-3.5" />
                {t.foscam.streamRetry}
              </button>
            </div>
          )}

          {/* Kam se míří. Kotví se k DLAŽDICI, ne k obrazu: během otáčení se obraz pod
              značkou posouvá, takže kotvení k obrazu by ji odneslo pryč z místa, kam
              uživatel klikl. Inline styl, protože poloha je dynamická a pulzování musí
              přežít `prefers-reduced-motion` (to globálně nuluje `animation-duration`). */}
          {aim.marker && (
            <span
              aria-hidden="true"
              className={cn(
                "pointer-events-none absolute z-20 -translate-x-1/2 -translate-y-1/2 rounded-full border-2",
                aim.marker.phase === "done" ? "border-status-ok" : "border-white"
              )}
              style={{
                left: aim.marker.at.x,
                top: aim.marker.at.y,
                width: aim.marker.phase === "done" ? 24 : 40,
                height: aim.marker.phase === "done" ? 24 : 40,
                opacity: aim.marker.phase === "done" ? 0.9 : 0.85,
                boxShadow: "0 0 0 2px rgba(0,0,0,0.45)",
                transition: "width 250ms ease-out, height 250ms ease-out, opacity 250ms ease-out",
              }}
            />
          )}

          {/* Jméno kamery — v mřížce ho nese hlavička wrapperu, tady chybí kontext. */}
          {fill && (
            <span
              className="absolute left-2 top-2 z-10 rounded-md bg-black/55 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm"
              style={chromeStyle}
            >
              {name}
            </span>
          )}

          {/* V mřížce je tlačítkem CELÁ dlaždice — zmenšená ikona sama by byla 28px terč,
              tedy pod minimem pro prst. Takhle je terč celá kamera (na telefonu ~183×103 px)
              a ikona v rohu je jen afordance. Zůstává jeden skutečný <button>, takže
              klávesnice i čtečky fungují; klikatelný <div> s vnořeným tlačítkem by byl
              nevalidní vnořený interaktivní prvek. */}
          {mode === "grid" && !spotlighted ? (
            <button
              type="button"
              className="absolute inset-0 z-10 flex items-start justify-end p-1 @[300px]:p-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              style={chromeStyle}
              aria-label={`${t.foscam.spotlight}: ${name}`}
              title={t.foscam.spotlight}
              onClick={(e) => {
                // Tažení ani pinch nad obrazem nesmí skončit otevřením spotlightu —
                // v mřížce je tlačítko přes celou dlaždici, takže by na něj propadlo
                // každé gesto digitálního zoomu.
                if (zoom.consumeDidDrag()) {
                  e.preventDefault();
                  return;
                }
                onSpotlight();
              }}
            >
              <span className={chromeChip}>
                <Maximize2 className={chromeIcon} />
              </span>
            </button>
          ) : (
            // Ve fullscreen režimech patří plocha nad obrazem PTZ ovládání a probuzení
            // chrome, ne zvětšování — proto tady zůstává jen rohové tlačítko.
            <div className="absolute right-2 top-2 z-10" style={chromeStyle}>
              <button
                type="button"
                className={cn(chromeChip, "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring")}
                aria-label={spotlighted ? t.foscam.spotlightClose : t.foscam.spotlight}
                title={spotlighted ? t.foscam.spotlightClose : t.foscam.spotlight}
                onClick={spotlighted ? onExitSpotlight : onSpotlight}
              >
                {spotlighted ? <Minimize2 className={chromeIcon} /> : <Maximize2 className={chromeIcon} />}
              </button>
            </div>
          )}
        </div>

        {showControls && (
          <CameraControls
            slug={slug}
            hold={hold}
            zoom={zoom}
            transport={stats.transport}
            measuredMs={stats.latencyMs}
            opacity={opacity}
            scrollAxis={mode === "stacked" && !spotlighted ? "stack" : "none"}
            onPoke={poke}
            onBusyChange={setControlsBusy}
          />
        )}
      </div>
    </>
  );
};

export default CameraTile;
